import { nanoid } from 'nanoid';
import { Exchange, makeResult, Operation } from 'urql';
import { filter, fromPromise, map, merge, mergeMap, pipe, share, Source } from 'wonka';

import { getOperationName } from '@/utils/urql';

/**
 * Exchange that intercepts operations targeting the productivity endpoint
 * (`/api/productivity`) and fetches them directly, bypassing Graphcache.
 *
 * Graphcache is configured with the main API schema and doesn't know about
 * productivity types (e.g. OpportunityInsight), so those operations must
 * skip the normalised cache entirely.
 */
const PRODUCTIVITY_PATH = '/api/productivity';

const isProductivityOperation = (operation: Operation): boolean => {
  const url: string | undefined = operation.context.url;
  return !!url && url.includes(PRODUCTIVITY_PATH);
};

const executeFetch = async (operation: Operation) => {
  const { url, fetchOptions: ctxFetchOptions } = operation.context;
  const fetchOptions =
    typeof ctxFetchOptions === 'function' ? ctxFetchOptions() : ctxFetchOptions || {};

  const operationName = getOperationName(operation.query);
  const urlWithOp = url + `?op=${operationName}`;

  const response = await fetch(urlWithOp, {
    method: 'POST',
    ...fetchOptions,
    headers: {
      'Content-Type': 'application/json',
      'x-request-id': nanoid(),
      'x-operation-name': operationName as string,
      ...fetchOptions.headers,
    },
    body: JSON.stringify({
      query: operation.query.loc?.source.body ?? '',
      variables: operation.variables ?? {},
      operationName,
    }),
  });

  const result = await response.json();

  return makeResult(operation, {
    data: result.data,
    errors: result.errors,
  } as any);
};

const productivityExchange: Exchange =
  ({ forward }) =>
  (ops$: Source<Operation>) => {
    const shared$ = pipe(ops$, share);

    // Operations targeting productivity — fetch directly, skip cache.
    const productivity$ = pipe(
      shared$,
      filter((op) => op.kind !== 'teardown' && isProductivityOperation(op)),
      mergeMap((op) =>
        pipe(
          fromPromise(executeFetch(op)),
          map((result) => result),
        ),
      ),
    );

    // Everything else — forward to the next exchange (cache, etc.).
    const other$ = pipe(
      shared$,
      filter((op) => op.kind === 'teardown' || !isProductivityOperation(op)),
      forward,
    );

    return merge([productivity$, other$]);
  };

export default productivityExchange;
